// src/admin/components/buildings/BuildingForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RiBuilding3Line,
  RiSaveLine,
  RiCloseLine,
  RiArrowLeftLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiMapPinLine,
  RiLayoutMasonryLine,
  RiBox3Line,
} from 'react-icons/ri';
import { createBuilding, getBuilding, updateBuilding } from '../../../models/buildingModel';
import { useTheme } from '../../../contexts/ThemeContext';

const BuildingForm = ({ isEdit = false }) => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  const [loading, setLoading]       = useState(isEdit);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched]       = useState({});

  // We keep the backend field names (totalFloors / totalApartments) for compatibility,
  // but show "Blocks" and "Showrooms" labels in the UI.
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    totalFloors: 1,
    totalApartments: 0,
  });

  const validationMessages = {
    name_required:      t('complex_name_required')  || 'Complex name is required',
    address_required:   t('complex_address_required')|| 'Address is required',
    invalid_floors:     t('invalid_blocks')          || 'Number of blocks must be at least 1',
    invalid_apartments: t('invalid_showrooms')      || 'Number of showrooms cannot be negative',
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!isEdit || !id) return;
      try {
        setLoading(true);
        const { building } = await getBuilding(id);
        setFormData({
          name:            building.name            || '',
          address:         building.address         || '',
          totalFloors:     building.totalFloors     || 1,
          totalApartments: building.totalApartments || 0,
        });
      } catch (err) {
        setError(err.message || 'Failed to load complex data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isEdit, id]);

  const validateField = (name, value) => {
    switch (name) {
      case 'name':           return !String(value).trim() ? validationMessages.name_required      : null;
      case 'address':        return !String(value).trim() ? validationMessages.address_required   : null;
      case 'totalFloors':    return value < 1             ? validationMessages.invalid_floors     : null;
      case 'totalApartments':return value < 0             ? validationMessages.invalid_apartments : null;
      default:               return null;
    }
  };

  const handleBlur = ({ target: { name, value } }) => {
    setTouched(p => ({ ...p, [name]: true }));
    setFormErrors(p => ({ ...p, [name]: validateField(name, value) }));
  };

  const handleChange = ({ target: { name, value, type, checked } }) => {
    const val = type === 'checkbox' ? checked : value;
    setFormData(p => ({ ...p, [name]: val }));
    if (touched[name]) setFormErrors(p => ({ ...p, [name]: validateField(name, val) }));
  };

  const validateForm = () => {
    const errs = {}; const touch = {};
    Object.keys(formData).forEach(k => { touch[k] = true; errs[k] = validateField(k, formData[k]); });
    setTouched(touch); setFormErrors(errs);
    return !Object.values(errs).some(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      document.querySelector('.error-field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name:            formData.name,
        address:         formData.address,
        totalFloors:     parseInt(formData.totalFloors),
        totalApartments: parseInt(formData.totalApartments),
      };
      isEdit ? await updateBuilding(id, payload) : await createBuilding(payload);
      navigate('/admin/buildings');
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} complex`);
      window.scrollTo(0, 0);
    } finally {
      setSaving(false);
    }
  };

  const renderField = (name, label, type = 'text', icon, placeholder, required = true, min = null, hint = null) => {
    const hasError = touched[name] && formErrors[name];
    const isValid  = touched[name] && !formErrors[name] && formData[name] !== '';
    return (
      <div className={hasError ? 'error-field' : ''}>
        <label className={`block mb-1.5 text-sm font-semibold ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <div className={`absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}>
            {icon}
          </div>
          {type === 'textarea' ? (
            <textarea name={name} value={formData[name]} onChange={handleChange} onBlur={handleBlur} rows={3}
              placeholder={placeholder}
              className={`w-full ps-10 pe-10 py-2.5 text-sm rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-red-500/40 ${isDarkMode?'bg-zinc-800 border-zinc-700 text-white':'bg-zinc-50 border-zinc-200 text-zinc-900'} ${hasError?'border-red-500':isValid?'border-emerald-500':''}`}
            />
          ) : (
            <input type={type} name={name} value={formData[name]} onChange={handleChange} onBlur={handleBlur} min={min}
              placeholder={placeholder}
              className={`w-full ps-10 pe-10 py-2.5 text-sm rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-red-500/40 ${isDarkMode?'bg-zinc-800 border-zinc-700 text-white':'bg-zinc-50 border-zinc-200 text-zinc-900'} ${hasError?'border-red-500':isValid?'border-emerald-500':''}`}
            />
          )}
          {hasError && <div className="absolute inset-y-0 end-0 flex items-center pe-3 text-red-500"><RiErrorWarningLine size={17}/></div>}
          {isValid  && <div className="absolute inset-y-0 end-0 flex items-center pe-3 text-emerald-500"><RiCheckLine size={17}/></div>}
        </div>
        {hasError ? (
          <p className="mt-1.5 text-xs text-red-500">{formErrors[name]}</p>
        ) : hint ? (
          <p className={`mt-1.5 text-xs ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}>{hint}</p>
        ) : null}
      </div>
    );
  };

  /* ── Loading ─────────────────────────────────────────────────────── */
  if (loading) return (
    <div dir={isRTL?'rtl':'ltr'} className={`rounded-xl border shadow-lg p-16 text-center ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>
      <div className="flex flex-col items-center gap-4">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-red-500 border-t-transparent"/>
        <p className={`text-lg font-medium ${isDarkMode?'text-zinc-300':'text-zinc-600'}`}>Loading complex data…</p>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════════════════════════ */
  return (
    <div dir={isRTL?'rtl':'ltr'} className={`rounded-xl border shadow-lg overflow-hidden ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>

      {/* Gradient accent */}
      <div className="h-1.5 w-full bg-gradient-to-r from-red-600 via-orange-500 to-red-600"/>

      {/* Header */}
      <div className={`p-5 md:p-6 border-b ${isDarkMode?'border-zinc-800 bg-zinc-900/50':'border-zinc-100 bg-zinc-50/80'}`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${isDarkMode?'bg-red-900/30 text-red-400':'bg-red-50 text-red-600'}`}>
              <RiBuilding3Line size={24}/>
            </div>
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode?'text-white':'text-zinc-900'}`}>
                {isEdit ? t('configure_showroom_park', 'Configure Showroom Park') : t('register_new_showroom_park', 'Register New Showroom Park')}
              </h2>
              <p className={`text-sm mt-0.5 ${isDarkMode?'text-zinc-400':'text-zinc-500'}`}>
                {isEdit ? t('update_complex_desc', 'Update the structural details of this complex.') : t('define_complex_desc', 'Define the location and capacity of your new logistics complex.')}
              </p>
            </div>
          </div>
          <button onClick={()=>navigate('/admin/buildings')}
            className={`p-2 rounded-full transition-colors shrink-0 ${isDarkMode?'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700':'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'}`}>
            <RiArrowLeftLine size={20} className="rtl:rotate-180"/>
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className={`mx-6 mt-6 p-4 rounded-lg flex items-start gap-3 ${isDarkMode?'bg-red-900/20 text-red-400 border border-red-900/40':'bg-red-50 text-red-700 border border-red-200'}`}>
          <RiErrorWarningLine className="text-xl shrink-0 mt-0.5"/>
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-6">
        <div className="max-w-3xl space-y-8">

          {/* Section: Identity */}
          <div className="space-y-5">
            <h3 className={`text-xs font-black uppercase tracking-widest pb-2 border-b ${isDarkMode?'text-zinc-500 border-zinc-800':'text-zinc-400 border-zinc-200'}`}>
              {t('identity_and_location', 'Identity & Location')}
            </h3>
            <div className="grid grid-cols-1 gap-5">
              {renderField('name', t('complex_park_name', 'Complex / Park Name'), 'text', <RiBuilding3Line size={17}/>, t('e_g_northside', 'e.g. Northside Logistics Hub'))}
              {renderField('address', t('physical_address', 'Physical Address'), 'textarea', <RiMapPinLine size={17}/>, t('enter_full_geographic_address', 'Enter the full geographic address'), true, null, t('used_for_mapping_desc', 'Used for mapping and generating official reports.'))}
            </div>
          </div>

          {/* Section: Capacity */}
          <div className="space-y-5">
            <h3 className={`text-xs font-black uppercase tracking-widest pb-2 border-b ${isDarkMode?'text-zinc-500 border-zinc-800':'text-zinc-400 border-zinc-200'}`}>
              {t('capacity_and_zoning', 'Capacity & Zoning')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {renderField('totalFloors',     t('total_blocks_zones', 'Total Blocks / Zones'), 'number', <RiLayoutMasonryLine size={17}/>, t('e_g_4', 'e.g. 4'), true, 1, t('how_many_blocks_desc', 'How many distinct blocks or rows does this park have?'))}
              {renderField('totalApartments', t('total_showrooms_label', 'Total Showrooms'),     'number', <RiBox3Line size={17}/>,          t('e_g_100', 'e.g. 100'), true, 0, t('total_showrooms_desc', 'Total number of individual showroom units in the complex.'))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className={`mt-10 pt-6 border-t flex justify-end gap-3 ${isDarkMode?'border-zinc-800':'border-zinc-200'}`}>
          <button type="button" onClick={()=>navigate('/admin/buildings')} disabled={saving}
            className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-colors ${isDarkMode?'bg-zinc-800 text-zinc-300 hover:bg-zinc-700':'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'}`}>
            <RiCloseLine className="inline me-1.5" size={16}/>
            {t('cancel')||'Cancel'}
          </button>
          <button type="submit" disabled={saving}
            className="px-6 py-2.5 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-500/20 transition-all flex items-center gap-2 min-w-[130px] justify-center">
            {saving ? (
              <><div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"/>{t('saving')||'Saving…'}</>
            ) : (
              <><RiSaveLine size={16}/>{isEdit ? t('update_park', 'Update Park') : t('save_complex', 'Save Complex')}</>
            )}
          </button>
        </div>
      </form>

      <style dangerouslySetInnerHTML={{__html:`
        @keyframes fadeIn { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out forwards; }
      `}}/>
    </div>
  );
};

export default BuildingForm;